let peerMetricsCtorPromise = null;
async function loadPeerMetricsConstructor() {
    if (!peerMetricsCtorPromise) {
        peerMetricsCtorPromise = import('@peermetrics/sdk')
            .then((module) => module.PeerMetrics ?? null)
            .catch((error) => {
            console.warn('PeerMetrics SDK is not available, skipping instrumentation', error);
            return null;
        });
    }
    return peerMetricsCtorPromise;
}
const getPeerConnectionFromTransport = (transport) => {
    if (!transport || typeof transport !== 'object') {
        return null;
    }
    if (transport instanceof RTCPeerConnection) {
        return transport;
    }
    const candidate = transport._pc
        ?? transport.pc;
    if (candidate && typeof candidate === 'object') {
        return candidate;
    }
    return null;
};
export class PeerMetricsManager {
    constructor(roomCode, room, serverHost) {
        this.roomCode = roomCode;
        this.room = room;
        this.serverHost = serverHost;
        this.peerMetrics = null;
        this.listenerCleanup = null;
        this.connections = {
            publisher: undefined,
            subscriber: undefined,
        };
    }
    async ensureSession({ userId, userName, role }) {
        if (typeof window === 'undefined') {
            return;
        }
        const apiKey = import.meta.env.VITE_REACT_APP_PEERMETRICS_API_KEY;
        if (!apiKey) {
            console.warn('PeerMetrics API key is not configured, skipping instrumentation');
            return;
        }
        if (!this.peerMetrics) {
            const normalizedRole = typeof role === 'string' ? role : role !== undefined && role !== null ? String(role) : undefined;
            try {
                const PeerMetricsConstructor = await loadPeerMetricsConstructor();
                if (!PeerMetricsConstructor) {
                    return;
                }
                const peerMetrics = new PeerMetricsConstructor({
                    apiKey,
                    apiRoot: import.meta.env.VITE_REACT_APP_PEERMETRICS_API_ROOT,
                    userId,
                    userName,
                    conferenceId: this.roomCode,
                    conferenceName: this.roomCode,
                    meta: normalizedRole ? { role: normalizedRole } : undefined,
                });
                await peerMetrics.initialize();
                this.peerMetrics = peerMetrics;
            }
            catch (error) {
                console.error('Failed to initialize PeerMetrics', error);
                this.peerMetrics = null;
                return;
            }
        }
        if (this.listenerCleanup) {
            return;
        }
        const engine = this.room?.engine;
        if (!engine) {
            console.warn('LiveKit engine is not ready, skipping PeerMetrics setup for now');
            return;
        }
        void this.updateServerAddress();
        const handleTransportsCreated = (publisher, subscriber) => {
            void this.registerTransport(publisher, 'publisher');
            void this.registerTransport(subscriber, 'subscriber');
        };
        engine.on?.('transportsCreated', handleTransportsCreated);
        this.listenerCleanup = () => {
            engine.off?.('transportsCreated', handleTransportsCreated);
        };
        const pcManager = engine.pcManager;
        if (pcManager?.publisher) {
            void this.registerTransport(pcManager.publisher, 'publisher');
        }
        if (pcManager?.subscriber) {
            void this.registerTransport(pcManager.subscriber, 'subscriber');
        }
    }
    async cleanup() {
        this.listenerCleanup?.();
        this.listenerCleanup = null;
        const peerMetrics = this.peerMetrics;
        if (!peerMetrics) {
            this.connections = {
                publisher: undefined,
                subscriber: undefined,
            };
            return;
        }
        const removals = [];
        Object.values(this.connections).forEach((connection) => {
            if (connection?.connectionId) {
                removals.push(peerMetrics.removeConnection({ connectionId: connection.connectionId }).catch((error) => {
                    console.warn('Failed to remove PeerMetrics connection', error);
                }));
            }
        });
        this.connections = {
            publisher: undefined,
            subscriber: undefined,
        };
        if (removals.length) {
            await Promise.all(removals);
        }
        try {
            await peerMetrics.endCall();
        }
        catch (error) {
            console.warn('Failed to end PeerMetrics session', error);
        }
        finally {
            this.peerMetrics = null;
        }
    }
    async registerTransport(transport, key) {
        if (!this.peerMetrics || !transport) {
            return;
        }
        await this.updateServerAddress();
        const peerConnection = getPeerConnectionFromTransport(transport);
        if (!peerConnection) {
            console.warn(`Unable to resolve RTCPeerConnection for ${key} transport`);
            return;
        }
        const existingConnection = this.connections[key];
        if (existingConnection?.connectionId) {
            try {
                await this.peerMetrics.removeConnection({ connectionId: existingConnection.connectionId });
            }
            catch (error) {
                console.warn(`Failed to remove existing PeerMetrics connection for ${key}`, error);
            }
        }
        try {
            const peerId = `${this.roomCode}-${key}`;
            const peerName = this.buildPeerName(key);
            const { connectionId } = await this.peerMetrics.addConnection({
                peerId,
                peerName,
                pc: peerConnection,
                isSfu: true,
                remote: true,
            });
            this.connections[key] = {
                connectionId,
                peerId,
            };
        }
        catch (error) {
            console.error(`Failed to register ${key} transport with PeerMetrics`, error);
        }
    }
    buildPeerName(key) {
        const fallback = key === 'publisher' ? 'LiveKit Publisher' : 'LiveKit Subscriber';
        const base = this.serverHost || fallback;
        if (this.serverAddress) {
            return `${base} (${this.serverAddress})`;
        }
        return base;
    }
    async updateServerAddress() {
        if (this.serverAddress) {
            return;
        }
        const engine = this.room?.engine;
        if (!engine?.getConnectedServerAddress) {
            return;
        }
        try {
            const address = await engine.getConnectedServerAddress();
            if (address) {
                this.serverAddress = address;
            }
        }
        catch (error) {
            console.warn('Failed to resolve LiveKit server address', error);
        }
    }
}
